<template>
  <div>
    <app-alert-in-page v-if="isWitnessEnabled" class="mb-4">
      <div class="d-flex">
        <p class="text--small">{{
            $t('project.signing-checklist.add.step2.Section2.witnessInfo.regular')
          }}
        </p>
        <p class="ml-1 text--small text--bold">
          {{ $t('project.signing-checklist.add.step2.Section2.witnessInfo.bold') }}
        </p>
      </div>
       <a
         class="app-link learnMoreLink text--small text--bold"
         href="https://help.closd.com"
         rel="noopener"
         target="_blank"
       >
          {{ $t('common.learnMore') }}
          <app-material-icon name="open_in_new" color="inherit" class="ml-1" size="xs" />
      </a>
    </app-alert-in-page>

    <v-row>
      <v-col cols="12">
        <div class="text-text-secondary">{{ $t('project.signing-checklist.add.step2.Section2.signatureTypeCaption') }}</div>
        <AppDownloadFile :link="learnMoreSignatureTypesLink" forceReader>
          <template #default="{ submitForm }">
            <a class="app-link learnMoreLink" @click="submitForm">
              {{ $t('project.signing-checklist.add.step2.Section2.learnMoreSignatureType') }}
              <app-material-icon name="open_in_new" color="inherit" size="xs" />
            </a>
          </template>
        </AppDownloadFile>
      </v-col>

      <v-col cols="12">
        <v-radio-group
          ref="signatureTypeRadioGroup"
          :model-value="signatureTypeFormField.$model"
          required
          :disabled="isEditingLimited"
          class="envelopeType__container"
          inline
          :error="signatureTypeFormField.$error"
          :error-messages="signatureTypeErrorMessages"
          @update:model-value="signatureTypeChange"
        >
          <template v-for="item in envelopeTypeOptions" :key="item.value">
            <AppTooltip :disabled="!item.disabled">
              <template #activator="{props}">
                <div v-bind="props">
                  <v-radio
                    :true-value="item.value"
                    :disabled="item.disabled"
                    class="envelopeType__card"
                    :class="{'envelopeType__card--active': item.value === signatureTypeFormField.$model}"
                    :data-locator="`${item.value.split('.').join('-')}-signature-type-card`"
                    readonly
                    @click="!item.disabled && signatureTypeChange(item.value)"
                  >
                    <template v-slot:label>
                      <h4 class="text-text-primary">{{ item.text }}</h4>
                      <p class="text-text-tertiary text--caption">
                        <span v-if="SIGNATURE_TYPE_WITH_AUTH.includes(item.value)" class="mr-1">
                          {{ $t('project.signing-checklist.add.step2.Section2.signingTypeAuthRequired') }}
                        </span>
                        <span v-if="item.value === SIGNATURE_TYPE_HANDWRITTEN">
                          {{ $t('project.signing-checklist.add.step2.Section2.signingTypeNoOrder') }}
                        </span>
                        <i18n-t
                          keypath="project.signing-checklist.add.step2.Section2.signingTypeMaxSize"
                          tag="span"
                          scope="global"
                        >
                          <app-filesize-display :value="getProviderMaxSizeInBytes(item.providerMaxSize)"/>
                        </i18n-t>
                      </p>
                    </template>
                  </v-radio>
                </div>
              </template>
              <span> {{ $t(`project.signing-checklist.add.step2.Section2.${textTooltipDisabledOption}`) }}</span>
            </AppTooltip>
          </template>
        </v-radio-group>
      </v-col>

      <v-col v-if="signatureTypeFormField.$model === SIGNATURE_TYPE_HANDWRITTEN" cols="12">
        <v-checkbox
          :model-value="hasMercuryOptionActivated"
          class="mt-0"
          density="compact"
          hide-details
          :label="$t('project.signing-checklist.add.step2.Section2.mercuryCaseLabel')"
          @update:model-value="mercuryOptionChange"
        />
      </v-col>
    </v-row>

    <SignerOrderSignatureTypeIncompatibilityWarningDialog
      v-if="signerOrderSignatureTypeIncompatibilityWarningDialog"
      @close="signerOrderSignatureTypeIncompatibilityWarningDialog = false"
      @confirm="confirmChangeSignatureType"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mapGetters, mapMutations, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { formFieldErrors, VuelidateFormField } from '@/common/utils/form'
import { BtoMB } from '@/common/utils/sizes'
import {
  ENVELOPE_TYPES,
  SIGNATURE_TYPE_DISPLAY_ORDER,
  SIGNATURE_TYPE_DOCUSIGN_QUALIFIED,
  SIGNATURE_TYPE_HANDWRITTEN,
  SIGNATURE_TYPE_WITH_AUTH,
} from '@/project/signing-checklist/constants'
import SignerOrderSignatureTypeIncompatibilityWarningDialog from '@/project/signing-checklist/dialogs/SignerOrderSignatureTypeIncompatibilityWarningDialog.vue'
import { SET_IMPORTED_FILE_MERCURY_OPTION } from '@/store/modules/signing-checklist/mutation_types'

type EnvelopeTypeOption = {
  disabled: boolean
  providerMaxSize: number
  text: string
  value: string
}

type DataType = {
  SIGNATURE_TYPE_WITH_AUTH: typeof SIGNATURE_TYPE_WITH_AUTH
  SIGNATURE_TYPE_HANDWRITTEN: typeof SIGNATURE_TYPE_HANDWRITTEN
  envelopeTypeOptions: EnvelopeTypeOption[]
  signerOrderSignatureTypeIncompatibilityWarningDialog: boolean
}

export default defineComponent({
  name: 'Section2',
  components: {
    AppDownloadFile,
    AppTooltip,
    SignerOrderSignatureTypeIncompatibilityWarningDialog,
  },
  props: {
    isEditingLimited: {
      type: Boolean,
      required: true,
    },
    signatureTypeFormField: {
      type: Object as PropType<VuelidateFormField>,
      required: true,
    },
    fileId: {
      type: Number,
      required: true,
    },
    fileSize: {
      type: Number,
      required: true,
    },
    hasSignersOrder: {
      type: Boolean,
      required: true,
    },
    hasMercuryOptionActivated: {
      type: Boolean,
      required: true,
    },
    envelopeType: {
      type: String,
      required: true,
    },
  },
  data (): DataType {
    return {
      SIGNATURE_TYPE_WITH_AUTH,
      SIGNATURE_TYPE_HANDWRITTEN,
      signerOrderSignatureTypeIncompatibilityWarningDialog: false,
      envelopeTypeOptions: [],
    }
  },
  computed: {
    ...mapState('signingChecklist', ['qualifiedDSWarningIsMuted']),
    ...mapState('room', ['currentRoom', 'roomProvidersPending']),
    ...mapGetters('room', ['isWitnessEnabled']),
    learnMoreSignatureTypesLink (): string {
      const locale = this.$i18n.locale as string
      return `${import.meta.env.VUE_APP_API_URL}/assets/pdf?file=signatures-types-${locale.toUpperCase()}`
    },
    signatureTypeErrorMessages (): Array<string> {
      return formFieldErrors(
        this.signatureTypeFormField,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.signing-checklist.add.step2.Section2.signatureTypeLabel') })],
        ],
      )
    },
    textTooltipDisabledOption (): string {
      return this.envelopeType === ENVELOPE_TYPES.MULTIPLE ? 'envelopeMultipleDSQOptionDisabledTooltip' : 'envelopeTypeOptionDisabledTooltip'
    },
  },
  methods: {
    ...mapMutations('signingChecklist', [SET_IMPORTED_FILE_MERCURY_OPTION]),
    signatureTypeChange (signatureType: string | null) {
      const shouldWarnAboutManualSignature = signatureType === SIGNATURE_TYPE_HANDWRITTEN && this.hasSignersOrder
      if (shouldWarnAboutManualSignature) {
        this.signerOrderSignatureTypeIncompatibilityWarningDialog = true
        return
      }
      const shouldWarnAboutQualifiedSignature = signatureType === SIGNATURE_TYPE_DOCUSIGN_QUALIFIED && !this.qualifiedDSWarningIsMuted
      if (shouldWarnAboutQualifiedSignature) {
        this.$emit('setQualifiedDSWarningDialog', true)
        this.$emit('setSelectSignatureType', this.signatureTypeFormField.$model)
      } else {
        this.$emit('setSelectSignatureType', signatureType)
      }
    },
    confirmChangeSignatureType () {
      this.signerOrderSignatureTypeIncompatibilityWarningDialog = false
      this.$emit('setSelectSignatureType', SIGNATURE_TYPE_HANDWRITTEN)
    },
    getProviderMaxSizeInBytes (providerMaxSize: number): number {
      // TODO ask the back to improve that
      return providerMaxSize * 1048576
    },
    mercuryOptionChange (value: boolean | null) {
      this[SET_IMPORTED_FILE_MERCURY_OPTION]({
        fileId: this.fileId,
        value: value,
      })
    },
    getEnvelopesTypeOptions () {
      const enveloppes: Record<string, EnvelopeTypeOption> = {}

      if (!this.roomProvidersPending && this.currentRoom.providers) {
        for (const provider of this.currentRoom.providers) {
          for (const type of provider.types) {
            const value = provider.provider + '.' + type
            const isDisabled = this.envelopeType === ENVELOPE_TYPES.MULTIPLE && value === SIGNATURE_TYPE_DOCUSIGN_QUALIFIED
            enveloppes[value] = {
              text: this.$t(`project.signing-checklist.add.step2.Section2.signingTypeSelect.${value}`),
              value,
              disabled: this.fileSize > provider.maxSize || isDisabled,
              providerMaxSize: BtoMB(provider.maxSize),
            }
          }
        }
      }

      this.envelopeTypeOptions = SIGNATURE_TYPE_DISPLAY_ORDER
        .map(signatureType => enveloppes[signatureType])
        .filter(e => e !== undefined)
    },

  },
  mounted () {
    this.getEnvelopesTypeOptions()
  },
  watch: {
    envelopeType (newValue) {
      this.envelopeTypeOptions = this.envelopeTypeOptions.map(sig =>
      sig.value === SIGNATURE_TYPE_DOCUSIGN_QUALIFIED ? { ...sig, disabled: newValue === ENVELOPE_TYPES.MULTIPLE } : sig,
      )
    },
  },
})
</script>

<style scoped lang="scss">
.envelopeType__container {
  :deep(.v-selection-control-group) {
    width: 100%;
    display: grid;
    gap: 24px;
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  }

  .envelopeType__card {
    width: 100%;
    justify-self: stretch;
    min-height: 72px;
    padding: 16px 24px;
    border: 1px solid rgb(var(--v-theme-border-secondary));
    border-radius: 8px;
    margin: 0;

    &--active {
      outline: 2px solid rgb(var(--v-theme-border-info-bold));
      outline-offset: -2px;
      background-color: rgb(var(--v-theme-surface-info));
    }

    :deep(.v-label){
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
    }
  }
}
</style>
